import { useState } from 'react'
import {
  CreateContract,
  ErrorMessage,
  Labs,
  ModalDialog,
  Mtds,
  templates,
  useAuth,
  useCreateContractMutation,
  SaveError,
  useGetLabsQuery,
  widgets
} from 'prove-common'
import {
  customizeValidator,
  Form,
  IChangeEvent,
  RJSFSchema,
  UiSchema
} from 'prove-common/rjsf'

export const createContractSchema = (labs: Labs): RJSFSchema => ({
  type: 'object',
  properties: {
    title: {
      type: 'string',
      title: 'Tittel',
      description: 'Gi kontrakten en kort og beskrivende tittel'
    },
    labId: {
      type: 'number',
      title: 'Laboratorium',
      description: 'Velg hvilket laboratorium kontrakten gjelder for',
      oneOf: labs?.map((lab) => ({
        const: lab.id,
        title: lab.name
      }))
    },
    startDate: {
      type: 'string',
      format: 'date-time',
      title: 'Startdato',
      description: 'Når skal kontrakten starte?'
    },
    endDate: {
      type: 'string',
      format: 'date-time',
      title: 'Sluttdato',
      description: 'Når skal kontrakten slutte?'
    }
  },
  required: ['title', 'labId']
})

export const createContractUiSchema: UiSchema<CreateContract> = {
  'ui:options': {
    allowWrap: true
  }
}

type CreateContractModalProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
}

export default function CreateContractModal({
  isOpen,
  setOpen
}: Readonly<CreateContractModalProps>) {
  const { userId } = useAuth()
  const {
    data: labs,
    isLoading,
    isError,
    isSuccess,
    error: labsError
  } = useGetLabsQuery(isOpen)
  const createContract = useCreateContractMutation()
  const [saveError, setSaveError] = useState<unknown>()
  const onSubmit = ({ formData }: IChangeEvent<CreateContract>): void => {
    if (formData != null && !createContract.isPending) {
      setSaveError(undefined)
      createContract
        .mutateAsync({
          ownerId: `user:${userId}`,
          ...formData
        })
        .then(() => {
          Mtds.toast.success('Kontrakten er opprettet')
          setOpen(false)
        })
        .catch((error) => {
          setSaveError(error)
        })
    }
  }
  const validator = customizeValidator<CreateContract>()
  return (
    <ModalDialog
      id='create-contract-modal'
      closedby='any'
      open={isOpen}
      onClose={() => setOpen(false)}
    >
      <Mtds.Button
        command={'close'}
        commandfor={'create-contract-modal'}
        aria-label={'Lukk'}
      />
      <Mtds.Prose>
        <Mtds.Heading>Opprett kontrakt</Mtds.Heading>
        <SaveError error={saveError} action='opprette kontrakten' />
        {isLoading && <Mtds.Skeleton />}
        {isError && <ErrorMessage error={labsError} />}
        {isSuccess && (
          <Form<CreateContract>
            noHtml5Validate
            schema={labs == null ? {} : createContractSchema(labs)}
            uiSchema={createContractUiSchema}
            widgets={widgets}
            templates={templates}
            showErrorList={false}
            validator={validator}
            onSubmit={onSubmit}
            experimental_defaultFormStateBehavior={{
              constAsDefaults: 'skipOneOf'
            }}
          >
            <Mtds.Flex className='mt-8'>
              <Mtds.Button command='close' commandfor='create-contract-modal'>
                Forkast
              </Mtds.Button>
              <Mtds.Button
                type='submit'
                data-variant='primary'
                aria-busy={createContract.isPending}
                disabled={createContract.isPending}
              >
                Opprett
              </Mtds.Button>
            </Mtds.Flex>
          </Form>
        )}
      </Mtds.Prose>
    </ModalDialog>
  )
}
